import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, TextField, Button, MenuItem, Select, FormControl, Checkbox, InputLabel, Paper, Box, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, CircularProgress, Backdrop, TablePagination, Snackbar, Alert,
    Dialog, DialogActions, DialogContent, DialogTitle, TableSortLabel, DialogContentText
} from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Insights, Delete, BlockTwoTone, ReplayTwoTone, Edit } from '@mui/icons-material';
import { doc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getAuth } from "firebase/auth";
import SearchIcon from '@mui/icons-material/Search';
import fetchData from '../../components/fetchData';
import getIdToken from '../../components/getIdToken';

const Users = () => {
    const [loading, setLoading] = useState('');
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [action, setAction] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [role, setRole] = useState('');
    const [creationTimeFrom, setCreationTimeFrom] = useState('');
    const [creationTimeTo, setCreationTimeTo] = useState('');
    const [lastSignedInFrom, setLastSignedInFrom] = useState('');
    const [lastSignedInTo, setlastSignedInTo] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const location = useLocation();
    const [sortColumn, setSortColumn] = useState('creationTime');
    const [sortOrder, setSortOrder] = useState('desc');
    const [openBlockDialog, setOpenBlockDialog] = useState(false);
    const [dialogUserId, setDialogUserId] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const hasFetched = useRef(false);

    const fetchUsers = async (filters = null) => {
        setLoading(true);

        try {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                console.error("User is not logged in.");
                return;
            }

            const token = await currentUser.getIdToken();

            // Build filters from input or URL
            const queryParams = filters
                ? { ...filters }
                : Object.fromEntries(new URLSearchParams(location.search).entries());

            // Set defaults for pagination and sorting
            const filtersToSend = {
                ...queryParams,
                page: filters?.page || page + 1,
                rowsPerPage: filters?.rowsPerPage || rowsPerPage,
                sortColumn: filters?.sortColumn || sortColumn || "creationTime",
                sortOrder: filters?.sortOrder || sortOrder || "desc",
            };

            // Fetch data from server
            const response = await fetch(`${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}listUsers`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(filtersToSend),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setUsers(data.users || []);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchUsers();
        }

    }, [page, rowsPerPage, location.search]);


    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allUserIds = users.map((user) => user.id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectOne = (userId) => {
        setSelectedUsers((prevSelected) =>
            prevSelected.includes(userId)
                ? prevSelected.filter((id) => id !== userId)
                : [...prevSelected, userId]
        );
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        const queryParams = new URLSearchParams();
        if (emailInput) queryParams.set("email", emailInput);
        if (role) queryParams.set("role", role);
        if (creationTimeFrom) queryParams.set("creationTimeFrom", creationTimeFrom);
        if (creationTimeTo) queryParams.set("creationTimeTo", creationTimeTo);
        if (lastSignedInFrom) queryParams.set("lastSignedInFrom", lastSignedInFrom);
        if (lastSignedInTo) queryParams.set("lastSignedInTo", lastSignedInTo);
        if (sortColumn) queryParams.set("sortColumn", sortColumn);
        if (sortOrder) queryParams.set("sortOrder", sortOrder);
        queryParams.set('page', newPage + 1);
        navigate({ search: queryParams.toString() });
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        localStorage.setItem('rowsPerPage', parseInt(event.target.value, 10));
        const queryParams = new URLSearchParams();
        if (emailInput) queryParams.set("email", emailInput);
        if (role) queryParams.set("role", role);
        if (creationTimeFrom) queryParams.set("creationTimeFrom", creationTimeFrom);
        if (creationTimeTo) queryParams.set("creationTimeTo", creationTimeTo);
        if (lastSignedInFrom) queryParams.set("lastSignedInFrom", lastSignedInFrom);
        if (lastSignedInTo) queryParams.set("lastSignedInTo", lastSignedInTo);
        if (sortColumn) queryParams.set("sortColumn", sortColumn);
        if (sortOrder) queryParams.set("sortOrder", sortOrder);
        queryParams.set('page', 1);
        navigate({ search: queryParams.toString() });
    };

    useEffect(() => {
        const savedRowsPerPage = localStorage.getItem('rowsPerPage');
        if (savedRowsPerPage) {
            setRowsPerPage(savedRowsPerPage); // Set the state to the saved value
        }
        // Cleanup function to reset rowsPerPage when the component unmounts
        return () => {
            localStorage.removeItem('rowsPerPage');
        };
    }, []);

    const handleSearch = () => {
        const queryParams = new URLSearchParams();
        if (emailInput) queryParams.set("email", emailInput);
        if (role) queryParams.set("role", role);
        if (creationTimeFrom) queryParams.set("creationTimeFrom", creationTimeFrom);
        if (creationTimeTo) queryParams.set("creationTimeTo", creationTimeTo);
        if (lastSignedInFrom) queryParams.set("lastSignedInFrom", lastSignedInFrom);
        if (lastSignedInTo) queryParams.set("lastSignedInTo", lastSignedInTo);
        queryParams.set("page", 1);

        // Update the URL with the new query parameters
        navigate({ search: queryParams.toString() });

        // Prepare filters to pass into fetchUsers
        const filters = {
            email: emailInput || null,
            role: role || null,
            creationTimeFrom: creationTimeFrom || null,
            creationTimeTo: creationTimeTo || null,
            lastSignedInFrom: lastSignedInFrom || null,
            lastSignedInTo: lastSignedInTo || null,
            page: 1,
            rowsPerPage,
        };

        // Fetch users with the filters
        fetchUsers(filters);
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const email = queryParams.get("email") || "";
        const role = queryParams.get("role") || "";
        const creationFrom = queryParams.get("creationTimeFrom") || "";
        const creationTo = queryParams.get("creationTimeTo") || "";
        const signedInFrom = queryParams.get("lastSignedInFrom") || "";
        const signedInTo = queryParams.get("lastSignedInTo") || "";
        const sortColumn = queryParams.get('sortColumn') || 'creationTime';
        const sortOrder = queryParams.get('sortOrder') || 'desc';
        const pageQuery = queryParams.get('page') || '1';

        if (!pageQuery) {
            pageQuery = '1';
        }
        queryParams.delete('page');

        const paramsToKeep = {};
        queryParams.forEach((value, key) => {
            if (value) {
                paramsToKeep[key] = value;
            }
        });

        let newUrlParams = new URLSearchParams(paramsToKeep).toString();
        newUrlParams += `&page=${pageQuery}`;
        const finalUrl = `${location.pathname}?${newUrlParams}`;
        if (location.search !== `?${newUrlParams}`) {
            navigate(finalUrl, { replace: true });
        }

        setEmailInput(email);
        setRole(role);
        setCreationTimeFrom(creationFrom);
        setCreationTimeTo(creationTo);
        setLastSignedInFrom(signedInFrom);
        setlastSignedInTo(signedInTo);
        setSortColumn(sortColumn);
        setSortOrder(sortOrder);
        const pageParam = parseInt(pageQuery, 10) - 1;
        setPage(pageParam);
        fetchUsers();
    }, [location.search]);

    const handleReset = () => {
        setEmailInput('');
        setRole('');
        setCreationTimeFrom('');
        setCreationTimeTo('');
        setLastSignedInFrom('');
        setlastSignedInTo('');

        // Reset the URL to only include the default page
        navigate("/dashboard/users?page=1", { replace: true });

        // Fetch users with no filters (defaults)
        fetchUsers({
            email: null,
            role: null,
            creationTimeFrom: null,
            creationTimeTo: null,
            lastSignedInFrom: null,
            lastSignedInTo: null,
            page: 1,
            rowsPerPage,
        });
    };

    // There is no Usecase for now, will develop this feature if needed...
    // const handleApplyAction = () => {
    //     selectedUsers.forEach((user) => {
    //         const { id, role, isBlocked } = user;
    //         handleDelete(id, role, isBlocked);
    //     });
    //     setAction(""); // Reset the action
    // };

    const handleBlock = (id, role, isBlocked, email) => {
        if (role === "admin") {
            setSnackbarMessage("Admin cannot be blocked.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        if (role === "user" && !isBlocked) {
            setDialogUserId(id); // Set dialog user ID for confirmation
            setOpenBlockDialog(true);
        } else {
            handleUserUpdate(id, role, { isBlocked: !isBlocked }, email);
        }
    };


    const confirmMarkAsBlock = () => {
        // Pass the stored email and dialogUserId to the update function
        const selectedUser = users.find(user => user.id === dialogUserId);
        if (!selectedUser) {
            console.error("Selected user not found.");
            return;
        }

        handleUserUpdate(dialogUserId, selectedUser.role, { isBlocked: true }, selectedUser.email);
        setOpenBlockDialog(false);
    };

    const handleUserUpdate = async (userId, role, updates, email) => {
        console.log("Reached to add an Entry");
        try {
            console.log("role is:", role);
            if (role !== 'admin') {
                const apiUrl = `${process.env.REACT_APP_API_BASE_URL}ai/writer/v1/user/firebase/${userId}`;
                console.log("apiUrl:", apiUrl);
                const response = await fetchData({
                    url: apiUrl,
                    method: 'PATCH',
                    body: { isBlocked: updates.isBlocked, }
                });
                console.log("response:", response);
                // const docRef = doc(db, "users", userId); // Using userId as the document ID
                // await setDoc(docRef, {
                //     email: email || null,
                //     isBlocked: updates.isBlocked,
                //     userId: userId,
                // }, { merge: true });
                const message = updates.isBlocked
                    ? "User has been successfully marked as Blocked!"
                    : "User has been successfully marked as UnBlocked!";
                setSnackbarMessage(message);
                setSnackbarSeverity("success");
                setSnackbarOpen(true);

                // Fetch the Users
                fetchUsers();
            }
        } catch (error) {
            setSnackbarMessage("Error updating user: " + error.message);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            console.error("Error adding user document:", error);
        }
    };

    const handleEdit = (id) => {
        console.log("Editing user with ID:", id);
        // Add logic to navigate to the edit page or display a modal
        const user = users.find((u) => u.id === id);
        setSelectedUser(user);
        setRole(user.role === 'admin' ? 'admin' : 'user');
        setOpenDialog(true);
    };


    const handleRoleChange = async () => {
        if (!selectedUser) return;
        try {
            setLoading(true);
            const auth = getAuth();

            // Prevent the current admin from changing their own role
            if (auth.currentUser && auth.currentUser.email === selectedUser.email) {
                setErrorMessage("You cannot change your own role.");
                return;
            }

            //const userDocRef = doc(db, 'users', selectedUser.id);

            if (role === 'admin') {
                // Update only the isAdmin field to true
                const apiUrl = `${process.env.REACT_APP_API_BASE_URL}ai/writer/v1/user/firebase/${selectedUser.id}`
                const updateResponse = await fetchData({
                    url: apiUrl,
                    method: 'PATCH',
                    body: { isAdmin: true }
                });
                console.log("updateResponse:", updateResponse);
                // await setDoc(userDocRef, {
                //     isAdmin: true,
                // }, { merge: true });
                //set in mysql
                const billingApiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/v1/user/${selectedUser.id}/grantAdmin`
                const mysqlUpdateResponse = await fetchData({
                    url: billingApiUrl,
                    method: 'POST',
                    body: { firebaseUid: selectedUser.id, isAdmin: true }
                });
                console.log("mysqlUpdateResponse:", mysqlUpdateResponse);
                // Call the Cloud Function to set user claims
                const response = await fetch(`${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}setUserRole`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${await getIdToken()}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ uid: selectedUser.id, role: 'admin' }),
                });

                if (!response.ok) {
                    throw new Error('Failed to set user role');
                }
            } else {
                // Update only the isAdmin field to false
                const apiUrl = `${process.env.REACT_APP_API_BASE_URL}ai/writer/v1/user/firebase/${selectedUser.id}`
                console.log("apiUrl:", apiUrl);
                const updateResponse = await fetchData({
                    url: apiUrl,
                    method: 'PATCH',
                    body: { isAdmin: false }
                });
                console.log("updateResponse:", updateResponse);

                //set in mysql
                const billingApiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/v1/user/${selectedUser.id}/grantAdmin`
                const mysqlUpdateResponse = await fetchData({
                    url: billingApiUrl,
                    method: 'POST',
                    body: { firebaseUid: selectedUser.id, isAdmin: false }
                });
                console.log("mysqlUpdateResponse:", mysqlUpdateResponse);

                // Call the Cloud Function to set user claims to 'user'
                const response = await fetch(`${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}setUserRole`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${await getIdToken()}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ uid: selectedUser.id, role: 'user' }),
                });

                if (!response.ok) {
                    throw new Error('Failed to set user role');
                }
            }

            setOpenDialog(false); // Close the dialog
            fetchUsers(); // Refresh the user list
        } catch (error) {
            console.error('Error updating user roles:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setErrorMessage('');
    };

    const handleSort = (column) => {
        const isAsc = sortColumn === column && sortOrder === 'asc';
        const newSortOrder = isAsc ? 'desc' : 'asc';
        setSortColumn(column);
        setSortOrder(newSortOrder);

        // Sorting logic
        const sortedUsers = [...users].sort((a, b) => {
            const aValue = new Date(a[column]);
            const bValue = new Date(b[column]);
            return newSortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });

        setUsers(sortedUsers);

        // Update Query Params
        const queryParams = new URLSearchParams(location.search);
        queryParams.set('sortColumn', column);
        queryParams.set('sortOrder', newSortOrder);
        queryParams.set('page', 1); // Reset to the first page when sorting

        navigate({ search: queryParams.toString() });
    };

    // Paginated Users
    const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const formatDate = (dateStr) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', timeZoneName: 'short' };
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('en-US', options).format(date);
    };

    // Handle the Case of Delete
    const handleDelete = (id, role, email) => {
        if (role === "admin") {
            setSnackbarMessage("Admin cannot be deleted.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }
        setDeleteUserId(id);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        setIsDeleting(true); // Start loading

        try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) {
                console.error("User is not logged in.");
                return;
            }

            const apiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/user/${deleteUserId}`;
            const response = await fetchData({
                url: apiUrl,
                method: 'DELETE',
            })
            
            // const token = await currentUser.getIdToken();
            // const response = await fetch(`${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}deleteUser`, {
            //     method: "POST",
            //     headers: {
            //         Authorization: `Bearer ${token}`,
            //         "Content-Type": "application/json",
            //     },
            //     body: JSON.stringify({ userId: deleteUserId }),
            // });

            if (!response.ok) {
                throw new Error(`HTTP error!`);
            }

            setSnackbarMessage("User and all associated data deleted successfully!");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            fetchUsers();
        } catch (error) {
            console.error("Error deleting user:", error);
            setSnackbarMessage("Error deleting user: " + error.message);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsDeleting(false);
            setOpenDeleteDialog(false); // Close dialog after operation completes
            setDeleteUserId(null);
        }
    };

    return (
        <Container maxWidth={false} sx={{ width: '100%', padding: 0, paddingBottom: '5px' }}>
            <Box alignItems="center" sx={{ paddingTop: '10px' }}>
                <Box item xs={4.5}>
                    <Typography variant="h5" gutterBottom paddingLeft={"6px"}>
                        Users
                    </Typography>
                </Box>
            </Box>
            <Paper elevation={3} sx={{ padding: '20px', marginBottom: '20px', marginTop: '20px' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        backgroundColor: '#D81B60',
                        width: '35px',
                        height: '35px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                        boxShadow: 3,
                    }}>
                    <SearchIcon sx={{ fontSize: 30, color: '#fff' }} />
                </Box>
                <Box container spacing={2} alignItems="center">
                    <Box item xs={12} sx={{ paddingLeft: '45px' }}>
                        <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                            Search
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginTop: '15px'
                    }}
                >
                    <TextField
                        fullWidth
                        label="Email"
                        variant="standard"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <FormControl fullWidth variant="standard">
                        <InputLabel id="role-label">Current Role</InputLabel>
                        <Select
                            labelId="role-label"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                        >
                            <MenuItem value="Admin">Admin</MenuItem>
                            <MenuItem value="User">User</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Creation Time From"
                        variant="standard"
                        type="date"
                        slotProps={{
                            inputLabel: { shrink: true },
                        }}
                        value={creationTimeFrom}
                        onChange={(e) => setCreationTimeFrom(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Creation Time To"
                        variant="standard"
                        type="date"
                        slotProps={{
                            inputLabel: { shrink: true },
                        }}
                        value={creationTimeTo}
                        onChange={(e) => setCreationTimeTo(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Last SignedIn From"
                        variant="standard"
                        type="date"
                        slotProps={{
                            inputLabel: { shrink: true },
                        }}
                        value={lastSignedInFrom}
                        onChange={(e) => setLastSignedInFrom(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Last SignedIn To"
                        variant="standard"
                        type="date"
                        slotProps={{
                            inputLabel: { shrink: true },
                        }}
                        value={lastSignedInTo}
                        onChange={(e) => setlastSignedInTo(e.target.value)}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: '16px', marginTop: '20px' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleSearch}
                        style={{ fontWeight: 'bold' }}
                    >
                        Search
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        onClick={handleReset}
                        style={{ fontWeight: 'bold' }}
                    >
                        Reset
                    </Button>
                </Box>
            </Paper>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 20, marginLeft: 5 }}>
                <FormControl variant="standard" style={{ marginRight: 10, minWidth: 150 }}>
                    <InputLabel id="action-label" shrink={true} sx={{ fontSize: '1.1rem', paddintLeft: '15px' }}>Action</InputLabel>
                    <Select
                        labelId="action-label"
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                        sx={{ width: 170, height: 28 }}
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {/* <MenuItem value="delete">Delete</MenuItem> */}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    color="primary"
                    // onClick={handleApplyAction}
                    disabled={selectedUsers.length === 0 || action === ''}
                    sx={{ height: 42 }}
                >
                    Apply
                </Button>
            </div>
            {loading ? (
                <Backdrop open={true} style={{ color: '#fff', zIndex: 1 }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            ) : (
                <TableContainer component={Paper} style={{ marginTop: 20 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={
                                            selectedUsers.length > 0 &&
                                            selectedUsers.length < users.length
                                        }
                                        checked={
                                            users.length > 0 &&
                                            selectedUsers.length === users.length
                                        }
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }} >
                                    <TableSortLabel
                                        active={sortColumn === 'email'}
                                        direction={sortOrder}
                                        onClick={() => handleSort('email')}
                                    >
                                        Email
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'domainCount'}
                                        direction={sortOrder}
                                        onClick={() => handleSort('domainCount')}
                                    >
                                        Domains#
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'role'}
                                        direction={sortOrder}
                                        onClick={() => handleSort('role')}
                                    >
                                        Role
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'creationTime'}
                                        direction={sortOrder}
                                        onClick={() => handleSort('creationTime')}
                                    >
                                        Created On
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'lastSignInTime'}
                                        direction={sortOrder}
                                        onClick={() => handleSort('lastSignInTime')}
                                    >
                                        Last Access
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedUsers?.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedUsers.includes(row.id)}
                                            onChange={() => handleSelectOne(row.id)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <a
                                            href={`mailto:${row.email}`}
                                            style={{
                                                textDecoration: 'none',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            {row.email}
                                        </a>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            onClick={() => navigate(`/dashboard/domains?email=${row.email}&page=1`)}
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            {row.domainCount}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        {row.role === "user" ? "User" : row.role === "admin" ? "Admin" : ""}
                                    </TableCell>
                                    <TableCell>{formatDate(row.creationTime)}</TableCell>
                                    <TableCell>{formatDate(row.lastSignInTime)}</TableCell>
                                    <TableCell align="center">
                                        <Button
                                            component={RouterLink}
                                            to={`/dashboard/users/stats/${row.id}`}
                                            state={{ user: row }}
                                            size="small"
                                            color="info"
                                            // onClick={() => {
                                            //     console.log(`Navigating to /dashboard/users/stats/${row.id}`);
                                            //     navigate(`/dashboard/users/stats/${row.id}`, { state: { user: row } });
                                            // }}
                                            onContextMenu={() => {
                                                sessionStorage.setItem(
                                                    `user_${row.id}`,
                                                    JSON.stringify(row)
                                                );
                                            }}
                                            startIcon={<Insights />}
                                            style={{ marginLeft: 10, fontWeight: 'bold' }}
                                        >
                                            Stats
                                        </Button>
                                        <Button
                                            size="small"
                                            color="success"
                                            onClick={() => handleEdit(row.id)}
                                            startIcon={<Edit />}
                                            style={{ marginLeft: 10, fontWeight: 'bold' }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            color="secondary"
                                            onClick={() => handleBlock(row.id, row.role, row.isBlocked, row.email)}
                                            startIcon={row.isBlocked ? <ReplayTwoTone /> : <BlockTwoTone />}
                                            style={{ marginLeft: 10, fontWeight: "bold" }}
                                        >
                                            {row.isBlocked ? "UnBlock" : "Block"}
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(row.id, row.role, row.email)}
                                            startIcon={<Delete />}
                                            style={{ marginLeft: 10, fontWeight: "bold" }}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Edit User Role</DialogTitle>
                <DialogContent>
                    {errorMessage && (
                        <div
                            style={{
                                marginBottom: '1rem',
                                marginTop: '1rem',
                                padding: '10px 15px',
                                backgroundColor: '#fdecea',
                                color: '#d93025',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                fontWeight: 'bold',
                                fontSize: '0.95rem',
                                border: '1px solid #f5c6cb',
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                                width="20px"
                                height="20px"
                                style={{ flexShrink: 0 }}
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm.75 5.5a.75.75 0 0 0-1.5 0v5a.75.75 0 0 0 1.5 0V7.5zm-.75 10.25a.875.875 0 1 0 0-1.75.875.875 0 0 0 0 1.75z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span>{errorMessage}</span>
                        </div>
                    )}
                    {selectedUser && (
                        <div>
                            <TableContainer component={Paper} elevation={3} style={{ marginTop: '1rem', borderRadius: '8px' }}>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 'bold', width: '40%', padding: '12px' }}>User ID</TableCell>
                                            <TableCell style={{ padding: '12px' }}>{selectedUser.id}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 'bold', padding: '12px' }}>Email</TableCell>
                                            <TableCell style={{ padding: '12px' }}>
                                                <a href={`mailto:${selectedUser.email}`} style={{ textDecoration: 'none' }}>
                                                    {selectedUser.email}
                                                </a>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 'bold', padding: '12px' }}>Account Created On</TableCell>
                                            <TableCell style={{ padding: '12px' }}>{formatDate(selectedUser.creationTime)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 'bold', padding: '12px' }}>Last Signed In</TableCell>
                                            <TableCell style={{ padding: '12px' }}>{formatDate(selectedUser.lastSignInTime)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 'bold', padding: '12px' }}>Role</TableCell>
                                            <TableCell style={{ padding: '12px' }}>
                                                <FormControl variant="standard" fullWidth>
                                                    <Select
                                                        labelId="role-label"
                                                        value={role}
                                                        onChange={(e) => setRole(e.target.value)}
                                                    >
                                                        <MenuItem value="user">User</MenuItem>
                                                        <MenuItem value="admin">Admin</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    )}
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25 }}>
                    <Button onClick={handleCloseDialog} color="secondary" variant="outlined" sx={{ fontWeight: "bold" }}>Cancel</Button>
                    <Button onClick={handleRoleChange} color="primary" variant="contained" sx={{ fontWeight: "bold" }}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for "Mark as Block" */}
            <Dialog
                open={openBlockDialog}
                onClose={() => setOpenBlockDialog(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Mark User as Blocked</DialogTitle>
                <DialogContent>
                    <DialogContentText
                        style={{
                            marginTop: '1rem',
                            padding: '10px 15px',
                            backgroundColor: '#fdecea',
                            color: '#d93025',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            border: '1px solid #f5c6cb',
                        }}
                    >
                        Marking this user as block will prevent them from signing into Xeo.
                        This will not remove their data. Do you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25 }}>
                    <Button onClick={() => setOpenBlockDialog(false)} color="secondary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Cancel
                    </Button>
                    <Button onClick={confirmMarkAsBlock} color="primary" variant="contained" sx={{ fontWeight: "bold" }}>
                        Mark as Blocked
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for "Mark as Delete" */}
            <Dialog
                open={openDeleteDialog}
                onClose={isDeleting ? null : () => setOpenDeleteDialog(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    Delete User
                    {isDeleting && <CircularProgress size={24} style={{ marginLeft: 10 }} />}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        style={{
                            marginTop: '1rem',
                            padding: '10px 15px',
                            backgroundColor: '#fdecea',
                            color: '#d93025',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            border: '1px solid #f5c6cb',
                        }}
                    >
                        {isDeleting ? (
                            "Deleting user and all associated data..."
                        ) : (
                            "Deleting this user will permanently remove their account and all associated data from Xeo. This includes projects, keywords, groups, domains, and templates. This action cannot be undone."
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25 }}>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        color="secondary"
                        variant="outlined"
                        sx={{ fontWeight: "bold" }}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        sx={{ fontWeight: "bold", position: 'relative' }}
                        disabled={isDeleting}
                    >
                        {isDeleting && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                        <span style={{ visibility: isDeleting ? 'hidden' : 'visible' }}>
                            Confirm Delete
                        </span>
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Handle Apply Actions */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, overflow: 'hidden', flexDirection: window.innerWidth < 600 ? 'column' : 'row' }}>
                <Button
                    variant="contained"
                    color="primary"
                    // onClick={handleApplyAction}
                    disabled={selectedUsers.length === 0 || action === ''}
                    style={{
                        marginTop: window.innerWidth < 600 ? '20px' : '0',
                        marginLeft: window.innerWidth < 600 ? '5px' : '0',
                        alignSelf: window.innerWidth < 600 ? 'flex-start' : 'center'
                    }}
                >
                    Apply
                </Button>
                <div style={{ flexGrow: 1 }} />
                <TablePagination
                    component="div"
                    count={users.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    style={{ paddingBottom: '20px', paddingTop: '10px', overflow: 'hidden' }}
                />
            </div>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={10000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </Container>
    );
};

export default Users;
